// ============================================================================
// AXIOM — liveBindings: which gift does what, declared here and shipped
// ----------------------------------------------------------------------------
// The grid maps the two axes of a gift — tier (how big one is) and quantity
// (how many arrived) — onto the five actions, so a creator toggles rather than
// types. Every number in a payload is ours; the one event-decided value goes
// through scaleWithQuantity, whose band must sit INSIDE the field's own schema
// bounds or publication refuses it.
//
// Design rules this grid follows (designing-live-actions.md §6):
//  - Within one exclusive group, (tier ∩ band) must not overlap — one open
//    band per tier, everything else closed with max.
//  - A bigger gift is a DIFFERENT effect, not a bigger number: SMALL is
//    spectacle, MEDIUM is pressure, LARGE is the sign duel, SUPREME is mayhem
//    (two groups on purpose, so both fire from one gift).
//  - Per-action budgets: the bindings on one action must sum under its
//    defaultLimits.maxPerMinute, or publication warns.
// ============================================================================

export const BINDING_DECLS = {
  // ── 無料層(繰り返せる行為) ────────────────────────────────────────────
  // The free tier is the top of the funnel (live-game-design-principles.md §2):
  // likes/comments/shares buy impressions, impressions fill the room, and a
  // full room is what makes a SUPREME worth throwing. These three bindings are
  // that entrance. prime_rain is the carrier because it is the one action
  // already proven safe in bulk: capped at 120 glyphs, COALESCE, COSMETIC.
  like_rain: {
    label: 'いいね50 → 素数の雨',
    description: 'いいねが50たまるごとに素数の雨が降ります。連打がそのまま画面に出ます。',
    // likeThreshold is also what the in-game gauge counts to: Portal.js reads it
    // from here rather than keeping a second copy, so moving this number moves
    // the bar with it. Two copies would eventually let the bar fill without any
    // rain arriving, which reads as the game ignoring the room — the exact
    // failure the gauge exists to prevent.
    trigger: { kind: 'LIKE', likeThreshold: 50 },
    action: { key: 'prime_rain', payload: { density: 1 } },
    limits: { maxPerMinute: 60 },
  },
  share_rain: {
    label: 'シェア → 素数の雨(大)',
    description: 'シェアすると濃い素数の雨が降ります。画面外の行為に、画面内の見返りを返します。',
    trigger: { kind: 'SHARE' },
    action: { key: 'prime_rain', payload: { density: 2 } },
    limits: { maxPerMinute: 20 },
  },
  chant_rain: {
    label: '「!prime」→ 素数の雨',
    description: 'コメント「!prime」で素数の雨が降ります。照合のみで本文は画面に出ません。既定はオフ — コメント導線は配信者が選んで入れます。',
    trigger: { kind: 'COMMENT', keyword: '!prime' },
    action: { key: 'prime_rain', payload: { density: 1 } },
    enabledByDefault: false,
    limits: { maxPerMinute: 30, perActorCooldownMs: 10000 },
  },

  follow_sparkle: {
    label: 'フォロー → 素数の雨(小)',
    description: '新規フォローで小さな素数の雨が降ります。',
    trigger: { kind: 'FOLLOW' },
    action: { key: 'prime_rain', payload: { density: 1 } },
    limits: { maxPerMinute: 30 },
  },

  gift_small_rain: {
    label: '小ギフト → 素数の雨',
    description: '小ギフト1〜3個で、個数と同じ濃さの素数の雨が降ります。',
    trigger: { kind: 'GIFT', tier: 'SMALL', quantityBand: { min: 1, max: 3 } },
    action: { key: 'prime_rain', payload: { density: 1 } },
    scaleWithQuantity: { field: 'density' },
    limits: { maxPerMinute: 60 },
  },
  gift_small_supply: {
    label: '小ギフト連打 → 補給',
    description: '小ギフト4個以上をまとめて送ると補給が届きます。種類は配信者が選べます。',
    trigger: { kind: 'GIFT', tier: 'SMALL', quantityBand: { min: 4 } },
    action: { key: 'drop_supply', payload: { kind: 'health' } },
    choices: [
      {
        choiceKey: 'supply_kind',
        label: '補給の種類',
        options: [
          { optionKey: 'health', label: '回復', patch: { kind: 'health' } },
          { optionKey: 'ammo', label: '弾薬', patch: { kind: 'ammo' } },
        ],
        default: 'health',
      },
    ],
    limits: { maxPerMinute: 20, perActorCooldownMs: 5000 },
  },

  gift_medium_wave: {
    label: '中ギフト → 増援',
    description: '中ギフト1〜4個で、個数と同じ数の増援が出ます。方向は配信者が選べます。',
    trigger: { kind: 'GIFT', tier: 'MEDIUM', quantityBand: { min: 1, max: 4 } },
    action: { key: 'spawn_wave', payload: { count: 1, side: 'right' } },
    scaleWithQuantity: { field: 'count' },
    choices: [
      {
        choiceKey: 'enemy_side',
        label: '敵の出る方向',
        options: [
          { optionKey: 'right', label: '右から', patch: { side: 'right' } },
          { optionKey: 'left', label: '左から', patch: { side: 'left' } },
          { optionKey: 'both', label: '両側から', patch: { side: 'both' } },
        ],
        default: 'right',
      },
    ],
    limits: { maxPerMinute: 30, perActorCooldownMs: 2000 },
  },
  gift_medium_token: {
    label: '中ギフト連打 → 演算子の差し入れ',
    description: '中ギフト5個以上をまとめて送ると、中位の演算子トークンが落ちます。',
    trigger: { kind: 'GIFT', tier: 'MEDIUM', quantityBand: { min: 5 } },
    action: { key: 'gift_token', payload: { tier: 2 } },
    limits: { maxPerMinute: 15, perActorCooldownMs: 5000 },
  },

  gift_large_hex: {
    label: '大ギフト → 符号の呪い',
    description: '大ギフトで基礎値 x の符号が反転します。プレイヤーは × か ÷ の共役で解除できます。長さは配信者が選べます。',
    trigger: { kind: 'GIFT', tier: 'LARGE' },
    action: { key: 'hex_sign', payload: { durationMs: 5000 } },
    choices: [
      {
        choiceKey: 'hex_length',
        label: '呪いの長さ',
        options: [
          { optionKey: 'short', label: '短い(4秒)', patch: { durationMs: 4000 } },
          { optionKey: 'long', label: '長い(6秒)', patch: { durationMs: 6000 } },
        ],
        default: 'short',
      },
    ],
    limits: { maxPerMinute: 4, perActorCooldownMs: 20000 },
  },

  // SUPREME fires BOTH, deliberately: separate exclusive groups.
  supreme_hex: {
    label: '特大ギフト → 最大の呪い',
    description: '特大ギフトで最長8秒の符号の呪い。増援と同時に発動します。',
    trigger: { kind: 'GIFT', tier: 'SUPREME' },
    action: { key: 'hex_sign', payload: { durationMs: 8000 } },
    exclusiveGroup: 'supreme_a',
    limits: { maxPerMinute: 2, perActorCooldownMs: 30000 },
  },
  supreme_wave: {
    label: '特大ギフト → 総攻撃',
    description: '特大ギフトで両側から最大の増援。呪いと同時に発動します。',
    trigger: { kind: 'GIFT', tier: 'SUPREME' },
    action: { key: 'spawn_wave', payload: { count: 5, side: 'both' } },
    exclusiveGroup: 'supreme_b',
    limits: { maxPerMinute: 2, perActorCooldownMs: 30000 },
  },
};
